import React from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';

import { toast } from 'material-react-toastify';

import { Loading } from '@/components/shared/Loading/Loading';
import {
  ContentStatus,
  ExploreBlockType,
  Gender,
  UpsertExploreBlockRequest,
} from '@addmile/backend-client';
import { zodResolver } from '@hookform/resolvers/zod';

import { ContentManagerService, useContentQueries } from '../../services/ContentManagerService';
import {
  transformBlockFormDataToRequest,
  transformBlockRequestToFormData,
  transformBlockToFormData,
} from '../../utils';
import { FieldKey, requiredFieldsByType } from './fieldConfig';
import AuthorFields from './form/AuthorFields';
import BlockFormHeader from './form/BlockFormHeader';
import CollectionsSelect from './form/CollectionsSelect';
import ContentField from './form/ContentField';
import MetaFields from './form/MetaFields';
import PersonalizationsField from './form/PersonalizationsField';
import QuizDetailsField from './form/QuizDetailsField';
import { StatusLanguageFields } from './form/StatusLanguageFields';
import { BlockFormData, blockFormSchema } from './schema';

interface BlockFormProps {
  slug?: string;
  initialData?: Partial<UpsertExploreBlockRequest>;
  onSubmit: (payload: { slug: string; data: UpsertExploreBlockRequest }) => void | Promise<void>;
  onCancel: () => void;
  editingSlug?: string;
  isLoading?: boolean;
  onContentChange?: (content: string) => void;
  onMetaChange?: (meta: { type: ExploreBlockType; videoUrl?: string }) => void;
  onSaveSuccess?: () => void;
}

export const BlockForm: React.FC<BlockFormProps> = ({
  slug,
  initialData,
  onSubmit,
  onCancel,
  editingSlug,
  isLoading = false,
  onContentChange,
  onMetaChange,
  onSaveSuccess,
}) => {
  const { useCollections, useBlock } = useContentQueries();
  const { data: collectionsData } = useCollections({ pageSize: 100 });
  const { data: existingBlock, isLoading: loadingBlock } = useBlock(slug || '', !!slug);

  const {
    control,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    formState: { errors },
  } = useForm<BlockFormData>({
    resolver: zodResolver(blockFormSchema) as Resolver<BlockFormData>,
    defaultValues: {
      slug: slug || '',
      title: '',
      type: ExploreBlockType.Action,
      imageUrl: '',
      intro: '',
      content: '[]',
      author: { name: '', title: '', photoUrl: '', gender: Gender.Male },
      collections: [],
      collectionPositions: {},
      showFeedback: false,
      status: ContentStatus.Offline,
      personalizations: [],
      language: 'en',
      details: '',
      generateAudio: true,
    } as BlockFormData,
  });

  const originalContentRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    try {
      if (existingBlock) {
        const formData = transformBlockToFormData(existingBlock, slug);

        reset(formData as BlockFormData, { keepDefaultValues: false });
        if ('content' in formData && typeof formData.content === 'string') {
          originalContentRef.current = formData.content;
        } else {
          originalContentRef.current = undefined;
        }
        if (formData.type) {
          setValue('type', formData.type, { shouldValidate: false });
        }
      } else if (initialData) {
        const formData = transformBlockRequestToFormData(initialData, slug);
        reset(formData as BlockFormData, { keepDefaultValues: false });
        if ('content' in formData && typeof formData.content === 'string') {
          originalContentRef.current = formData.content;
        } else {
          originalContentRef.current = undefined;
        }
        if (formData.type) {
          setValue('type', formData.type, { shouldValidate: false });
        }
      }
    } catch (error) {
      console.error('Error loading form data:', error);
    }
  }, [existingBlock, initialData, slug, reset, setValue]);

  const watchedContent = watch('content');
  const watchedType = watch('type');
  const watchedVideoUrl = watch('videoUrl');

  React.useEffect(() => {}, [watchedType]);

  React.useEffect(() => {
    if (onContentChange && watchedContent) {
      onContentChange(watchedContent);
    }
  }, [watchedContent, onContentChange]);

  React.useEffect(() => {
    if (onMetaChange && watchedType) {
      onMetaChange({ type: watchedType, videoUrl: watchedVideoUrl });
    }
  }, [watchedType, watchedVideoUrl, onMetaChange]);

  const { useBindCollectionBlock, useUnbindCollectionBlock } = useContentQueries();
  const bindMutation = useBindCollectionBlock();
  const unbindMutation = useUnbindCollectionBlock();

  const getErrorMessage = (err: unknown): string => {
    const e = err as {
      response?: { data?: { message?: string; error?: string } };
      message?: string;
    };
    return (
      e?.response?.data?.message || e?.response?.data?.error || e?.message || 'An error occurred'
    );
  };

  const handleFormSubmit = async (blockFormData: BlockFormData) => {
    const errors: string[] = [];

    try {
      const blockRequestData = transformBlockFormDataToRequest(blockFormData);
      const currentContent =
        'content' in blockFormData && typeof blockFormData.content === 'string'
          ? blockFormData.content
          : undefined;

      const contentChanged =
        !slug || originalContentRef.current === undefined
          ? true
          : currentContent !== originalContentRef.current;

      if (blockFormData.type === ExploreBlockType.Article) {
        blockRequestData.generateAudio = contentChanged;
      } else {
        delete (blockRequestData as Partial<UpsertExploreBlockRequest>).generateAudio;
      }

      await onSubmit({ slug: blockFormData.slug, data: blockRequestData });
      const selectedCollections: string[] = blockFormData.collections || [];
      const blockSlug = blockFormData.slug;

      if (blockSlug) {
        try {
          if (slug) {
            const currentBindings = await ContentManagerService.getCollectionsBlocks(blockSlug);
            const existingBindings =
              currentBindings?.items?.filter((item) => item.blockSlug === blockSlug) || [];
            const existingCollectionSlugs = existingBindings.map((item) => item.collectionSlug);
            const toBind = selectedCollections.filter(
              (collectionSlug) => !existingCollectionSlugs.includes(collectionSlug),
            );

            const toUnbind = existingCollectionSlugs.filter(
              (collectionSlug) => !selectedCollections.includes(collectionSlug),
            );

            if (toBind.length > 0) {
              const bindResults = await Promise.allSettled(
                toBind.map((collectionSlug) =>
                  bindMutation.mutateAsync({
                    collectionSlug,
                    blockSlug,
                  }),
                ),
              );
              bindResults.forEach((result, index) => {
                if (result.status === 'rejected') {
                  errors.push(
                    `Failed to bind collection "${toBind[index]}": ${getErrorMessage(result.reason)}`,
                  );
                }
              });
            }

            if (toUnbind.length > 0) {
              const unbindResults = await Promise.allSettled(
                toUnbind.map((collectionSlug) =>
                  unbindMutation.mutateAsync({
                    collectionSlug,
                    blockSlug,
                  }),
                ),
              );
              unbindResults.forEach((result, index) => {
                if (result.status === 'rejected') {
                  errors.push(
                    `Failed to unbind collection "${toUnbind[index]}": ${getErrorMessage(result.reason)}`,
                  );
                }
              });
            }
          } else {
            if (selectedCollections.length > 0) {
              const bindResults = await Promise.allSettled(
                selectedCollections.map((collectionSlug) =>
                  bindMutation.mutateAsync({
                    collectionSlug,
                    blockSlug,
                  }),
                ),
              );
              bindResults.forEach((result, index) => {
                if (result.status === 'rejected') {
                  errors.push(
                    `Failed to bind collection "${selectedCollections[index]}": ${getErrorMessage(result.reason)}`,
                  );
                }
              });
            }
          }
        } catch (error) {
          errors.push(`Failed to load collection bindings: ${getErrorMessage(error)}`);
        }
      }

      if (errors.length > 0) {
        const errorMessage = errors.join('\n');
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }

      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      throw error;
    }
  };

  if (loadingBlock) return <Loading />;

  const attemptSave = async () => {
    const valid = await trigger();
    if (!valid) {
      const fields = Object.keys(errors);
      if (fields.length > 0) {
        toast.error(`Required fields: ${fields.join(', ')}`);
      }
      return;
    }
    try {
      const values = getValues() as BlockFormData;
      await handleFormSubmit(values);
      toast.success('Block saved successfully');
    } catch (error) {
      console.error('Error saving block:', error);
    }
  };

  const currentType = watch('type');
  const typeRequired = currentType ? requiredFieldsByType[currentType] : new Set<FieldKey>();

  const collections = collectionsData?.collections || [];

  const groups: { needs: string[]; node: React.ReactNode }[] = [
    {
      needs: ['title', 'slug', 'imageUrl', 'intro'],
      node: (
        <MetaFields
          control={control}
          errors={errors}
          isEdit={!!slug}
          blockSlug={slug}
          setValue={setValue}
        />
      ),
    },
    { needs: ['author'], node: <AuthorFields control={control} /> },
    {
      needs: ['collections'],
      node: (
        <div>
          <label className="block text-sm font-semibold text-neutral-70 mb-2">
            Collections{' '}
            <span className="text-sm font-normal text-neutral-50">
              (Content will appear in all selected collections)
            </span>
          </label>
          <Controller
            name={'collections'}
            control={control}
            render={({ field }) => (
              <Controller
                name={'collectionPositions'}
                control={control}
                render={({ field: positionsField }) => (
                  <CollectionsSelect
                    value={field.value || []}
                    onChange={(newCollections) => {
                      field.onChange(newCollections);
                      const currentPositions = positionsField.value || {};
                      const newPositions = Object.fromEntries(
                        Object.entries(currentPositions).filter(([slug]) =>
                          newCollections.includes(slug),
                        ),
                      );
                      if (
                        Object.keys(newPositions).length !== Object.keys(currentPositions).length
                      ) {
                        setValue('collectionPositions', newPositions);
                      }
                    }}
                    collections={collections}
                    collectionPositions={positionsField.value || {}}
                    onPositionChange={(collectionSlug, position) => {
                      setValue('collectionPositions', {
                        ...(positionsField.value || {}),
                        [collectionSlug]: position,
                      });
                    }}
                  />
                )}
              />
            )}
          />
        </div>
      ),
    },
    {
      needs: ['personalizations'],
      node: <PersonalizationsField watch={watch} setValue={setValue} />,
    },
    {
      needs: ['status', 'language', 'showFeedback'],
      node: <StatusLanguageFields control={control} errors={errors} />,
    },
    {
      needs: ['details'],
      node: <QuizDetailsField control={control} />,
    },
  ];

  const visible = groups.filter(
    (g) => typeRequired && g.needs.some((n) => typeRequired.has(n as FieldKey)),
  );

  return (
    <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
      <BlockFormHeader
        onCancel={onCancel}
        editingSlug={editingSlug}
        watch={watch}
        attemptSave={attemptSave}
        isLoading={isLoading}
      />

      <div className="bg-neutral-10 rounded-lg p-4">
        <h2 className="text-xl font-semibold text-neutral-70 mb-4">General Info</h2>
        {visible.map((g, i) => (
          <React.Fragment key={i}>{g.node}</React.Fragment>
        ))}
      </div>

      <ContentField control={control} />
    </form>
  );
};

export default BlockForm;
